import React from "react";

type FormattedNumberInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange"> & {
  value: number;
  onValueChange: (value: number) => void;
  allowDecimal?: boolean;
  showZero?: boolean;
};

export function FormattedNumberInput({
  value,
  onValueChange,
  allowDecimal = false,
  showZero = false,
  ...rest
}: FormattedNumberInputProps): React.ReactElement {
  const [draftValue, setDraftValue] = React.useState(() => formatGroupedNumericInput(value, { allowDecimal, showZero }));

  React.useEffect(() => {
    setDraftValue(formatGroupedNumericInput(value, { allowDecimal, showZero }));
  }, [allowDecimal, showZero, value]);

  return (
    <input
      {...rest}
      type="text"
      inputMode={allowDecimal ? "decimal" : "numeric"}
      value={draftValue}
      onChange={(event) => {
        const sanitized = sanitizeFormattedNumericInput(event.target.value, allowDecimal);
        if (sanitized === "") {
          setDraftValue("");
          onValueChange(0);
          return;
        }

        const parsed = Number(sanitized);
        if (Number.isNaN(parsed)) {
          return;
        }

        setDraftValue(formatGroupedNumericInput(parsed, { allowDecimal, showZero }));
        onValueChange(parsed);
      }}
    />
  );
}

export function sanitizeFormattedNumericInput(raw: string, allowDecimal: boolean): string {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return "";
  }

  let sanitized = trimmed.replace(/,/g, "").replace(/[^\d.]/g, "");
  if (!allowDecimal) {
    sanitized = sanitized.replace(/\./g, "");
  }

  const firstDecimalIndex = sanitized.indexOf(".");
  if (firstDecimalIndex !== -1) {
    sanitized = sanitized.slice(0, firstDecimalIndex + 1) + sanitized.slice(firstDecimalIndex + 1).replace(/\./g, "");
  }

  return sanitized;
}

export function formatGroupedNumericInput(
  value: number,
  options: {
    allowDecimal?: boolean;
    showZero?: boolean;
  } = {},
): string {
  const { allowDecimal = false, showZero = false } = options;
  if (!Number.isFinite(value) || (!showZero && value === 0)) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: allowDecimal ? 2 : 0,
    minimumFractionDigits: 0,
  }).format(value);
}
